<script>
import { computed } from 'vue';

import moment from 'moment';

import {
  DAYS_SHORTCUTS,
  transpose,
} from '@/utils/calendarUtils';

export default {
  props: {
    month: {
      type: Number,
      default: moment().month(),
    },
    year: {
      type: Number,
      default: moment().year(),
    },
    firstDayOfWeek: {
      type: String,
      validator: (d) => DAYS_SHORTCUTS.includes(d),
      default: 'mo',
    },
  },
  setup(props) {
    // Month
    const currentMonth = computed(
      () => (props.month < 12 ? props.month : moment.unix(props.month).month()),
    );
    const monthAdded = computed(() => `0${currentMonth.value + 1}`.slice(-2));
    const monthName = computed(() => moment(monthAdded.value, 'MM').format('MMMM'));

    // Year
    const currentYear = computed(
      () => (props.year.toString().length > 4 ? moment.unix(props.year).year() : props.year),
    );

    // Days
    const days = computed(
      () => moment(`${currentYear.value}-${monthAdded.value}`, 'YYYY-MM').daysInMonth(),
    );
    const daysOffset = computed(() => DAYS_SHORTCUTS.indexOf(props.firstDayOfWeek));

    const getDate = (day) => moment(
      { year: currentYear.value, month: currentMonth.value, day },
    ).unix();

    const weekdayNames = computed(() => transpose(moment.weekdaysShort().map((d) => d.replace(/\./g, '')), daysOffset.value));

    const calculateOffset = (day) => (moment(
      { year: currentYear.value, month: currentMonth.value, day },
    ).day() + 7 - daysOffset.value) % 7;

    const monthOffset = (day) => `${day === 1 ? ` month-day--offset-${calculateOffset(day)}` : ''}`;

    const firstOfMonth = (day) => day === 1;
    const lastOfMonth = (day) => day === days.value;

    const firstOfWeek = (day) => calculateOffset(day) === 0;
    const lastOfWeek = (day) => calculateOffset(day) === 6;

    return {
      getDate,
      days,
      weekdayNames,

      currentMonth,
      currentYear,

      monthOffset,
      monthName,

      firstOfMonth,
      lastOfMonth,

      firstOfWeek,
      lastOfWeek,
    };
  },
};
</script>
<template>
  <div class="calendar-month">
    <slot
      name="header"
      :month-name="monthName"
      :current-year="currentYear"
    />
    <div class="week-names">
      <span
        v-for="name in weekdayNames"
        :key="name"
        class="week-day"
      >{{ name }}</span>
    </div>
    <div class="flex flex-wrap">
      <div
        v-for="day in days"
        :key="day"
        :class="`month-day${monthOffset(day)}`"
      >
        <slot
          name="days"
          :day="day"
          :get-date="getDate"
          :first-of-month="firstOfMonth"
          :last-of-month="lastOfMonth"
          :first-of-week="firstOfWeek"
          :last-of-week="lastOfWeek"
        />
      </div>
    </div>
  </div>
</template>
<style lang="postcss" scoped>
.calendar-month {
  --month-day-width: calc(100%/7);
  @apply w-full flex flex-col;

  .week-names {
    @apply flex justify-between;

    .week-day {
      @apply text-base-500 mb-2.5 text-xs uppercase text-center;
      width: var(--month-day-width);
    }
  }
  .month-day {
    width: var(--month-day-width);
    display: flex;
    justify-content: center;
    /* Offsets */
    &--offset-1 { margin-left: calc(1 * var(--month-day-width)); }
    &--offset-2 { margin-left: calc(2 * var(--month-day-width)); }
    &--offset-3 { margin-left: calc(3 * var(--month-day-width)); }
    &--offset-4 { margin-left: calc(4 * var(--month-day-width)); }
    &--offset-5 { margin-left: calc(5 * var(--month-day-width)); }
    &--offset-6 { margin-left: calc(6 * var(--month-day-width)); }
  }
}
</style>
